import { defineComponent as _defineComponent } from 'vue';
import { createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode } from "vue";
const _hoisted_1 = { class: "min-h-screen bg-gray-50 font-roboto" };
const _hoisted_2 = {
    key: 0,
    class: "min-h-screen flex items-center justify-center"
};
const _hoisted_3 = { class: "text-center" };
const _hoisted_4 = {
    key: 0,
    class: "max-w-md mx-auto"
};
const _hoisted_5 = { class: "text-gray-600 mb-4" };
const _hoisted_6 = { key: 0 };
const _hoisted_7 = { key: 1 };
const _hoisted_8 = { key: 2 };
const _hoisted_9 = { key: 3 };
const _hoisted_10 = { key: 4 };
const _hoisted_11 = { class: "max-w-md mx-auto" };
const _hoisted_12 = { class: "max-w-7xl mx-auto p-0 sm:p-6" };
const _hoisted_13 = {
    key: 0,
    class: "text-center text-gray-600 bg-white rounded-lg p-8"
};
const _hoisted_14 = {
    key: 1,
    class: "grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-8"
};
const _hoisted_15 = { class: "lg:sticky lg:top-6 lg:self-start border-b-8 lg:border-b-0 border-gray-300" };
import { inject, ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useCartStore, useCheckoutStore } from '@shared';
import { RestaurantApiClient, OrderOverview, ContactDataModal, TimeSelectionModalStore, CheckoutRightSidebar, DeliveryAddressModal } from '@shared';
import { useProjectConfig } from '@shared';
import { useTheme } from '@shared/composables/useTheme';
// Initialize API Client
export default /*@__PURE__*/ _defineComponent({
    __name: 'CheckoutPage',
    setup(__props) {
        const { config } = useProjectConfig();
        const { theme } = useTheme();
        const withOpacity = (hexColor, opacity) => {
            const hex = (hexColor || '').replace('#', '');
            if (!/^[0-9a-fA-F]{6}$/.test(hex))
                return hexColor;
            const alpha = Math.round(Math.min(1, Math.max(0, opacity)) * 255).toString(16).padStart(2, '0');
            return `#${hex}${alpha}`;
        };
        const warningColor = computed(() => theme.value.colors.warning || theme.value.colors.primary);
        const primaryColor = computed(() => theme.value.colors.primary || '#2563eb');
        const primaryButtonStyle = computed(() => ({
            backgroundColor: primaryColor.value
        }));
        const spinnerStyle = computed(() => ({
            borderBottomColor: primaryColor.value
        }));
        const menuLinkStyle = computed(() => ({
            color: primaryColor.value
        }));
        const warningBadgeStyle = computed(() => ({
            backgroundColor: withOpacity(warningColor.value, 0.13)
        }));
        const legacyRuntimeConfig = globalThis?.window?.projectConfig || {};
        const resolvedProjectId = config?.project?.id ||
            config?.restaurant?.projectId ||
            legacyRuntimeConfig?.restaurant?.projectId ||
            'unknown-project';
        const resolvedRestaurantName = config?.restaurant?.name ||
            legacyRuntimeConfig?.restaurant?.name ||
            'Restaurant';
        const resolvedApiBaseUrl = config?.deployment?.api?.baseUrl ||
            legacyRuntimeConfig?.app?.api?.baseUrl ||
            '/api';
        const resolvedPublicApiBaseUrl = config?.deployment?.api?.publicUrl ||
            legacyRuntimeConfig?.app?.api?.publicUrl ||
            '/public';
        const resolvedProjectDomain = config?.deployment?.domains?.production ||
            legacyRuntimeConfig?.app?.domains?.production ||
            '';
        const apiClient = new RestaurantApiClient({
            name: resolvedRestaurantName,
            projectIdentifier: resolvedProjectId,
            apiBaseUrl: resolvedApiBaseUrl,
            publicApiBaseUrl: resolvedPublicApiBaseUrl,
            customHeaders: {
                'X-Project-Domain': resolvedProjectDomain,
                'X-Restaurant-ID': resolvedProjectId
            }
        });
        // ✅ SIMPLIFIED: Main stores
        const cartStore = useCartStore();
        const checkoutStore = useCheckoutStore();
        let router;
        try {
            router = useRouter();
        }
        catch (error) {
            console.warn('⚠️ CheckoutPage: useRouter() failed, falling back to injected router', error);
        }
        if (!router) {
            router = inject('router', undefined);
        }
        const navigateTo = async (path) => {
            if (!router) {
                console.error('❌ CheckoutPage: Router instance missing, cannot navigate to', path);
                return;
            }
            await router.push(path);
        };
        // ✅ SIMPLIFIED: Local component state only
        const deliveryAdditionalInfo = ref("");
        const contactModalOpen = ref(false);
        const addressModalOpen = ref(false);
        const timeModalOpen = ref(false);
        const loading = ref(false);
        const initialLoading = ref(true); // Loading state for first pricing request
        const loadingTimeout = ref(false); // Timeout error state
        // requestStatus is now managed centrally in useOrderPricing via checkoutStore
        // ✅ LOKALE OPERATING HOURS STORAGE
        const localOperatingHours = ref([]);
        const orderCreated = ref(false);
        const createdOrder = ref(null);
        const error = ref(null);
        // Time selection - erweitert für den neuen Slider
        const timeSelection = ref({
            startTime: "",
            endTime: "",
            time: "" // Für Backward-Kompatibilität
        });
        // Payment method
        const paymentMethod = ref("manual");
        // Customer data
        const customer = ref({
            name: cartStore.customerData.name || "",
            phone: cartStore.customerData.phone || "",
            email: cartStore.customerData.email || "",
            address: {
                street: cartStore.deliveryAddress.street || "",
                city: cartStore.deliveryAddress.city || "",
                postalCode: cartStore.deliveryAddress.postalCode || "",
            },
            deliveryInstructions: deliveryAdditionalInfo.value || "",
        });
        // ✅ SIMPLIFIED: Order type sync with store
        const localOrderType = computed({
            get: () => checkoutStore.orderType,
            set: (value) => checkoutStore.setOrderType(value)
        });
        // ✅ EVENT-BASED: Restaurant status - reactive to pricing changes
        const isRestaurantClosed = ref(true);
        const restaurantClosedMessage = ref('');
        // Update restaurant status when pricing changes (event-based for reliability)
        const updateRestaurantStatus = () => {
            const pricingData = checkoutStore.pricingData?.value;
            const restaurantStatus = pricingData?.data?.restaurantStatus;
            const openingHoursValidation = checkoutStore.openingHoursInfo;
            const wasRestaurantClosed = isRestaurantClosed.value;
            // Check restaurant closed status
            const newIsRestaurantClosed = pricingData?.code === 'RESTAURANT_CLOSED' ||
                pricingData?.isOpen === false ||
                (openingHoursValidation ? openingHoursValidation.isWithinHours === false : false) ||
                checkoutStore.error !== null;
            const newRestaurantClosedMessage = openingHoursValidation?.message ||
                restaurantStatus?.message ||
                (pricingData?.code === 'RESTAURANT_CLOSED' ? pricingData?.message : '') ||
                'Aktuell geschlossen';
            console.log('🔍 CheckoutPage updateRestaurantStatus:', {
                wasRestaurantClosed,
                newIsRestaurantClosed,
                newRestaurantClosedMessage,
                pricingDataCode: pricingData?.code,
                pricingDataIsOpen: pricingData?.isOpen,
                checkoutStoreError: checkoutStore.error,
                hasPricingData: !!pricingData,
                trigger: 'updateRestaurantStatus_called'
            });
            // Update reactive values
            isRestaurantClosed.value = newIsRestaurantClosed;
            restaurantClosedMessage.value = newRestaurantClosedMessage;
            // Log status change
            if (wasRestaurantClosed !== newIsRestaurantClosed) {
                console.log(`🏪 Restaurant status changed: ${wasRestaurantClosed ? 'CLOSED' : 'OPEN'} → ${newIsRestaurantClosed ? 'CLOSED' : 'OPEN'}`);
            }
        };
        // ✅ SIMPLIFIED: Format price helper with better precision
        const formatPrice = (value) => {
            const numValue = typeof value === 'string' ? parseFloat(value) : value;
            if (isNaN(numValue))
                return '0,00 €';
            return new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(numValue);
        };
        // ✅ DEBUG: Track template visibility conditions
        const templateDebugInfo = computed(() => {
            const debugInfo = {
                initialLoading: initialLoading.value,
                loadingTimeout: loadingTimeout.value,
                requestStatus: checkoutStore.requestStatus,
                checkoutStoreLoading: checkoutStore.isLoading,
                // Template conditions
                showInitialLoading: initialLoading.value,
                showRequestError: checkoutStore.requestStatus !== null && checkoutStore.requestStatus !== 200 && initialLoading.value,
                showTimeoutError: loadingTimeout.value && initialLoading.value,
                showMainContent: !initialLoading.value,
                // Error screen priority (request error has priority over timeout error)
                activeErrorScreen: checkoutStore.requestStatus !== null && checkoutStore.requestStatus !== 200 && initialLoading.value ? 'REQUEST_ERROR' :
                    loadingTimeout.value && initialLoading.value ? 'TIMEOUT_ERROR' :
                        initialLoading.value ? 'LOADING' : 'MAIN_CONTENT'
            };
            console.log('🔍 CheckoutPage: Template visibility debug:', debugInfo);
            return debugInfo;
        });
        // Watch template debug info for changes
        watch(templateDebugInfo, (newInfo, oldInfo) => {
            if (newInfo.activeErrorScreen !== oldInfo?.activeErrorScreen) {
                console.log('🎭 CheckoutPage: Active screen changed:', {
                    from: oldInfo?.activeErrorScreen || 'INITIAL',
                    to: newInfo.activeErrorScreen,
                    reason: newInfo.showRequestError ? `HTTP ${newInfo.requestStatus}` :
                        newInfo.showTimeoutError ? 'Timeout' : 'Success'
                });
            }
        }, { immediate: true });
        // ✅ Separate watches for better debugging
        watch(() => checkoutStore.isLoading, (newIsLoading, oldIsLoading) => {
            console.log('🔄🔄🔄 CheckoutPage: isLoading changed', {
                loadingChange: `${oldIsLoading} → ${newIsLoading}`,
                requestStatus: checkoutStore.requestStatus,
                initialLoading: initialLoading.value,
                shouldHide: !newIsLoading && checkoutStore.requestStatus === 200 && initialLoading.value,
                timestamp: new Date().toISOString()
            });
            // When loading ends and request was successful, hide initial loading
            if (!newIsLoading && checkoutStore.requestStatus === 200 && initialLoading.value) {
                console.log('✅ CheckoutPage: Request successful - hiding initialLoading NOW');
                initialLoading.value = false;
            }
        }, { immediate: true });
        watch(() => checkoutStore.requestStatus, (newRequestStatus, oldRequestStatus) => {
            console.log('🔄🔄🔄 CheckoutPage: requestStatus changed', {
                statusChange: `${oldRequestStatus} → ${newRequestStatus}`,
                isLoading: checkoutStore.isLoading,
                initialLoading: initialLoading.value,
                shouldShowError: newRequestStatus !== null && newRequestStatus !== 200,
                timestamp: new Date().toISOString()
            });
            // If we have an error status and not loading anymore, show error screen
            if (newRequestStatus !== null && newRequestStatus !== 200 && !checkoutStore.isLoading && initialLoading.value) {
                console.log('❌ CheckoutPage: Error detected - keeping initialLoading for error screen', {
                    requestStatus: newRequestStatus,
                    errorType: newRequestStatus === 0 ? 'Network Error' : `HTTP ${newRequestStatus}`
                });
                // Keep initialLoading = true to show error screen
            }
        }, { immediate: true });
        // ✅ SIMPLIFIED: Mount - just initialize pricing with timeout handling
        // ✅ EVENT-BASED: Listen for pricing loading events
        const handlePricingLoadingEvent = (event) => {
            const { loading, projectId, finalStatus, finalCode } = event.detail;
            console.log('📡 CheckoutPage: Received orderPricingLoading event', {
                loading,
                projectId,
                finalStatus,
                finalCode,
                initialLoading: initialLoading.value,
                requestStatus: checkoutStore.requestStatus
            });
            // When loading ends and request was successful, hide initial loading
            if (!loading && checkoutStore.requestStatus === 200 && initialLoading.value) {
                console.log('✅ CheckoutPage: Pricing completed successfully - hiding initial loading via EVENT');
                initialLoading.value = false;
            }
            else if (!loading && checkoutStore.requestStatus !== 200 && checkoutStore.requestStatus !== null) {
                console.log('❌ CheckoutPage: Pricing completed with error - keeping loading for error screen via EVENT', {
                    requestStatus: checkoutStore.requestStatus
                });
            }
        };
        // ✅ EVENT-BASED: Listen for operating hours events
        const handleOperatingHoursEvent = (event) => {
            const { operatingHours, projectId, source } = event.detail;
            // ✅ LOKALE SPEICHERUNG: Extract und speichere Operating Hours
            if (operatingHours && operatingHours.length > 0) {
                const extractedHours = operatingHours.flatMap((daySchedule) => {
                    // Check for nested hours structure (original expected format)
                    if (daySchedule.hours && Array.isArray(daySchedule.hours)) {
                        return daySchedule.hours.map((timeRange) => ({
                            open: timeRange.open || timeRange.start, // Support both open/close and start/end
                            close: timeRange.close || timeRange.end
                        }));
                    }
                    // Check for flat structure (actual API format for operating hours)
                    else if (daySchedule.open && daySchedule.close) {
                        return [{
                                open: daySchedule.open,
                                close: daySchedule.close
                            }];
                    }
                    // Check for flat structure with start/end (delivery schedule format)
                    else if (daySchedule.start && daySchedule.end) {
                        return [{
                                open: daySchedule.start,
                                close: daySchedule.end
                            }];
                    }
                    return [];
                });
                if (extractedHours.length > 0) {
                    localOperatingHours.value = extractedHours;
                }
            }
            // Simply forward the operatingHoursUpdated event for components like SingleTimeSlider
            const forwardedEvent = new CustomEvent('operatingHoursUpdated', {
                detail: {
                    operatingHours,
                    source: `forwarded_from_${source}`,
                    timestamp: new Date().toISOString(),
                    projectId
                }
            });
            window.dispatchEvent(forwardedEvent);
        };
        onMounted(async () => {
            console.log("🔄 Simplified CheckoutPage mounted:", {
                itemCount: cartStore.items.length,
                orderType: checkoutStore.orderType,
                currentTotal: checkoutStore.total,
                initialRequestStatus: checkoutStore.requestStatus
            });
            // ✅ NEW: Parse URL parameters for discount code
            const urlParams = new URLSearchParams(window.location.search);
            const urlDiscountCode = (urlParams.get('code') || '').trim().toUpperCase();
            if (urlDiscountCode) {
                console.log('🔗 CheckoutPage: Discount code detected in URL:', urlDiscountCode);
                // Set flag that code came from URL
                checkoutStore.setDiscountFromUrl(urlDiscountCode);
            }
            // Listen for pricing loading events
            window.addEventListener('orderPricingLoading', handlePricingLoadingEvent);
            // ✅ Listen for operating hours events
            window.addEventListener('operatingHoursAvailable', handleOperatingHoursEvent);
            // Set timeout for loading screen (15 seconds)
            const loadingTimeoutId = setTimeout(() => {
                if (initialLoading.value) {
                    console.warn('⚠️ CheckoutPage: Loading timeout reached - showing error screen');
                    loadingTimeout.value = true;
                }
            }, 15000); // 15 seconds timeout
            try {
                // ✅ CRITICAL: Sync customer address to cartStore BEFORE first pricing calculation
                // This ensures delivery zone calculation works from the start
                syncAddressToCart();
                if (cartStore.items.length > 0) {
                    console.log('🔄 CheckoutPage: Starting initial pricing calculation with items:', cartStore.items.length);
                    await checkoutStore.calculatePricing();
                    if (urlDiscountCode) {
                        console.log('🎟️ CheckoutPage: Applying URL discount code automatically after pricing:', urlDiscountCode);
                        await checkoutStore.applyDiscountCode(urlDiscountCode);
                    }
                    // ✅ FORCE UPDATE: Ensure openingHoursInfo is available after calculation
                    await new Promise(resolve => setTimeout(resolve, 100)); // Small delay for reactivity
                    console.log('🕐 CheckoutPage: Opening hours info after calculation:', {
                        hasOpeningHoursInfo: !!checkoutStore.openingHoursInfo,
                        openingHoursData: checkoutStore.openingHoursInfo,
                        isWithinHours: checkoutStore.openingHoursInfo?.isWithinHours
                    });
                    // Status is now managed centrally in checkoutStore.requestStatus
                    console.log('✅ CheckoutPage: Initial pricing calculation completed', {
                        subtotal: checkoutStore.subtotal,
                        total: checkoutStore.total,
                        requestStatus: checkoutStore.requestStatus
                    });
                }
                else {
                    console.warn('⚠️ CheckoutPage: No items in cart - hiding loading screen');
                    initialLoading.value = false;
                    clearTimeout(loadingTimeoutId); // Clear timeout if no items
                }
                // Initial restaurant status update
                updateRestaurantStatus();
            }
            catch (error) {
                console.error('❌ CheckoutPage: Error during initial load:', error);
                // Status is managed centrally in checkoutStore, check for network errors
                if (error.message?.includes('ERR_CONNECTION_REFUSED')) {
                    loadingTimeout.value = true; // Network error - use timeout screen
                    console.warn('⚠️ CheckoutPage: Network error detected - showing timeout error screen');
                }
                // HTTP errors are handled by useOrderPricing and available via checkoutStore.requestStatus
            }
            finally {
                // Clear the timeout
                clearTimeout(loadingTimeoutId);
                // Don't automatically set initialLoading = false here
                // Let the watch on checkoutStore.isLoading handle it based on success/error status
                console.log('✅ CheckoutPage: onMounted finally block completed', {
                    loadingTimeout: loadingTimeout.value,
                    requestStatus: checkoutStore.requestStatus,
                    checkoutStoreLoading: checkoutStore.isLoading,
                    initialLoadingStillActive: initialLoading.value,
                    note: 'initialLoading will be controlled by isLoading watch'
                });
            }
        });
        // ✅ SIMPLIFIED: Watch for cart changes and recalculate
        watch(() => cartStore.items, async (newItems, oldItems) => {
            const newLength = newItems?.length || 0;
            const oldLength = oldItems?.length || 0;
            console.log('🔄 CheckoutPage: Cart changed', {
                oldLength,
                newLength,
                initialLoading: initialLoading.value,
                itemsChanged: newItems !== oldItems
            });
            // If cart was empty and now has items, and we're still loading, trigger calculation
            if (oldLength === 0 && newLength > 0 && initialLoading.value) {
                console.log('🔄 CheckoutPage: Cart populated during loading - starting pricing calculation');
                try {
                    await checkoutStore.calculatePricing(true);
                    console.log('✅ CheckoutPage: Late cart loading calculation completed');
                }
                catch (error) {
                    console.error('❌ CheckoutPage: Error during late cart calculation:', error);
                }
            }
            else if (newLength > 0) {
                try {
                    await checkoutStore.calculatePricing(true); // Force recalculation
                    console.log('✅ CheckoutPage: Recalculation completed after cart change');
                }
                catch (error) {
                    console.error('❌ CheckoutPage: Error during cart recalculation:', error);
                }
            }
            else {
                checkoutStore.clearPricing();
            }
        }, { immediate: false, deep: true });
        // Watch for order type changes
        watch(() => checkoutStore.orderType, async (newOrderType) => {
            console.log('🔄 CheckoutPage: Order type changed to', newOrderType);
            if (cartStore.items.length > 0) {
                try {
                    await checkoutStore.calculatePricing(true); // Force recalculation
                    console.log('✅ CheckoutPage: Order type recalculation completed');
                }
                catch (error) {
                    console.error('❌ CheckoutPage: Error during order type recalculation:', error);
                }
            }
        });
        // ✅ NEW: Watch for delivery address changes (for delivery orders only)
        watch(() => ({
            street: cartStore.deliveryAddress.street,
            city: cartStore.deliveryAddress.city,
            postalCode: cartStore.deliveryAddress.postalCode
        }), async (newAddress, oldAddress) => {
            // Only trigger recalculation for delivery orders when address becomes complete
            if (checkoutStore.orderType === 'delivery' && cartStore.items.length > 0) {
                const isComplete = newAddress.street?.trim() && newAddress.city?.trim() && newAddress.postalCode?.trim();
                const wasIncomplete = !oldAddress?.street?.trim() || !oldAddress?.city?.trim() || !oldAddress?.postalCode?.trim();
                if (isComplete && (wasIncomplete || newAddress.street !== oldAddress?.street)) {
                    console.log('🏠 CheckoutPage: Delivery address populated/changed - recalculating pricing', {
                        oldAddress,
                        newAddress
                    });
                    try {
                        await checkoutStore.calculatePricing(true);
                        console.log('✅ CheckoutPage: Address-based recalculation completed');
                    }
                    catch (error) {
                        console.error('❌ CheckoutPage: Error during address recalculation:', error);
                    }
                }
            }
        }, { deep: true, immediate: false });
        // ✅ EVENT-BASED: Watch for restaurant status changes
        watch(() => checkoutStore.pricingData, (newPricingData, oldPricingData) => {
            console.log('🏪 CheckoutPage: Pricing data changed - updating restaurant status', {
                oldCode: oldPricingData?.code,
                newCode: newPricingData?.code,
                oldIsOpen: oldPricingData?.isOpen,
                newIsOpen: newPricingData?.isOpen
            });
            updateRestaurantStatus();
        }, { deep: true });
        // Watch for checkout store error changes
        watch(() => checkoutStore.error, (newError, oldError) => {
            console.log('🏪 CheckoutPage: Error state changed - updating restaurant status', {
                oldError,
                newError
            });
            updateRestaurantStatus();
        });
        // ✅ NEW: Watch requestStatus changes for debugging
        watch(() => checkoutStore.requestStatus, (newStatus, oldStatus) => {
            console.log('🔍 CheckoutPage: requestStatus changed', {
                oldStatus,
                newStatus,
                shouldShowError: newStatus !== null && newStatus !== 200,
                initialLoading: initialLoading.value,
                loadingTimeout: loadingTimeout.value,
                templateWillShow: newStatus !== null && newStatus !== 200 && initialLoading.value ? 'REQUEST_ERROR_SCREEN' :
                    loadingTimeout.value && initialLoading.value ? 'TIMEOUT_ERROR_SCREEN' :
                        initialLoading.value ? 'LOADING_SCREEN' : 'MAIN_CONTENT'
            });
            // Log when error screen should be shown
            if (newStatus !== null && newStatus !== 200) {
                console.warn('⚠️ CheckoutPage: Error status detected - should show error screen', {
                    status: newStatus,
                    initialLoading: initialLoading.value,
                    willShowErrorScreen: initialLoading.value,
                    errorType: newStatus === 0 ? 'Network Error' :
                        newStatus === 404 ? 'Not Found' :
                            newStatus >= 500 ? 'Server Error' :
                                newStatus >= 400 ? 'Client Error' : 'Unknown'
                });
            }
            else if (newStatus === 200) {
                console.log('✅ CheckoutPage: Success status - should hide loading/error screens');
            }
        }, { immediate: true });
        // ✅ HELPER: Sync customer address to cartStore
        // This ensures delivery zone calculation always works
        const syncAddressToCart = () => {
            // Try to sync from customer.value.address first
            if (customer.value.address.street) {
                cartStore.deliveryAddress.street = customer.value.address.street;
                cartStore.deliveryAddress.city = customer.value.address.city;
                cartStore.deliveryAddress.postalCode = customer.value.address.postalCode;
                console.log('🔄 syncAddressToCart: Synced from customer.value.address:', cartStore.deliveryAddress);
            }
            else if (!cartStore.deliveryAddress.street) {
                // If customer.value.address is empty AND cartStore is also empty, warn
                console.log('⚠️ syncAddressToCart: No address available in customer or cartStore');
            }
            else {
                // Address is already in cartStore, no need to sync
                console.log('✅ syncAddressToCart: Address already in cartStore:', cartStore.deliveryAddress);
            }
        };
        // ✅ EVENT-BASED: Handle address saved from modal
        const handleAddressSaved = async (address) => {
            console.log('🏠 CheckoutPage: Address saved event received', address);
            // ✅ CRITICAL: Synchronize address to cartStore BEFORE calculating pricing
            // The watcher might not have run yet due to async timing
            if (address) {
                cartStore.deliveryAddress.street = address.street || '';
                cartStore.deliveryAddress.city = address.city || '';
                cartStore.deliveryAddress.postalCode = address.postalCode || '';
                console.log('🏠 CheckoutPage: Address synchronized to cartStore:', {
                    cartAddress: cartStore.deliveryAddress
                });
            }
            // Only recalculate for delivery orders with items in cart
            if (checkoutStore.orderType === 'delivery' && cartStore.items.length > 0) {
                console.log('🏠 CheckoutPage: Triggering pricing recalculation after address save...');
                try {
                    await checkoutStore.calculatePricing(true);
                }
                catch (error) {
                    console.error('❌ Error recalculating pricing after address save:', error);
                }
            }
        };
        // ✅ SIMPLIFIED: Sync customer data with cart store
        // IMPORTANT: immediate:true ensures address is synced on page load
        // Only sync if address has actual values to prevent overwriting
        watch(() => customer.value, (newCustomer) => {
            cartStore.customerData.name = newCustomer.name;
            cartStore.customerData.phone = newCustomer.phone;
            cartStore.customerData.email = newCustomer.email;
            // ✅ CRITICAL: Only sync address if it has a street (prevent overwriting with empty values)
            if (newCustomer.address.street) {
                cartStore.deliveryAddress.street = newCustomer.address.street;
                cartStore.deliveryAddress.city = newCustomer.address.city;
                cartStore.deliveryAddress.postalCode = newCustomer.address.postalCode;
                console.log('🔄 CheckoutPage Watcher: Customer address synced to cartStore:', {
                    address: cartStore.deliveryAddress
                });
            }
            else {
                console.log('⚠️ CheckoutPage Watcher: Skipped address sync (empty address in customer)', {
                    customerAddress: newCustomer.address,
                    cartAddress: cartStore.deliveryAddress
                });
            }
        }, { deep: true, immediate: true });
        // ✅ REMOVED: Old address watcher (replaced by @saved event)
        // The watcher approach didn't work due to Vue's in-place object mutation
        // Now using explicit @saved event from DeliveryAddressModal instead
        // ✅ SIMPLIFIED: Cart management
        async function updateItemQuantity(configId, newQuantity) {
            cartStore.updateQuantity(configId, newQuantity);
            // ✅ CRITICAL: Sync address before pricing recalculation
            syncAddressToCart();
            if (cartStore.items.length > 0) {
                try {
                    await checkoutStore.calculatePricing();
                }
                catch (error) {
                    console.error('❌ Error updating item quantity pricing:', error);
                }
            }
        }
        async function removeItem(configId) {
            cartStore.removeItem(configId);
            // ✅ CRITICAL: Sync address before pricing recalculation
            syncAddressToCart();
            if (cartStore.items.length > 0) {
                try {
                    await checkoutStore.calculatePricing();
                }
                catch (error) {
                    console.error('❌ Error removing item pricing:', error);
                }
            }
        }
        // Watch pickupTime and deliveryTime for changes and log them
        watch(() => [checkoutStore.pickupTime, checkoutStore.deliveryTime], (newValues, oldValues) => {
            const [newPickup, newDelivery] = newValues || ['', ''];
            const [oldPickup, oldDelivery] = oldValues || ['', ''];
            console.log('[CheckoutPage] pickupTime/deliveryTime changed:', {
                oldPickup,
                newPickup,
                oldDelivery,
                newDelivery,
                pricingData: checkoutStore.pricingData
            });
        }, { immediate: true });
        // Payment method selection
        function handlePaymentMethodSelected(method, config) {
            paymentMethod.value = method;
            console.log("Payment method selected:", method, config);
        }
        // ✅ Get selected time for order submission
        function getSelectedTimeForOrder() {
            // Priority 1: Store-based selected time (central time management)
            const selectedTime = checkoutStore.orderType === "delivery"
                ? checkoutStore.selectedDeliveryTime
                : checkoutStore.selectedPickupTime;
            if (selectedTime) {
                return selectedTime;
            }
            // Priority 2: Legacy timeSelection.time (backward compatibility)
            if (timeSelection.value?.time) {
                return timeSelection.value.time;
            }
            // Priority 3: No specific time selected = ASAP
            return undefined;
        }
        // ✅ Parse time string to Date object for API
        function parseTimeStringToDate(timeString) {
            const today = new Date();
            // Handle format "HH:MM" (e.g., "20:22")
            const timeMatch = timeString.match(/^(\d{1,2}):(\d{2})$/);
            if (timeMatch) {
                const [, hours, minutes] = timeMatch;
                const date = new Date(today);
                date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
                // If the time is in the past today, assume it's for tomorrow
                if (date < today) {
                    date.setDate(date.getDate() + 1);
                }
                return date;
            }
            // Handle format "HH:MM Uhr" (e.g., "20:22 Uhr")
            const timeWithUhrMatch = timeString.match(/^(\d{1,2}):(\d{2})\s*Uhr$/i);
            if (timeWithUhrMatch) {
                const [, hours, minutes] = timeWithUhrMatch;
                const date = new Date(today);
                date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
                // If the time is in the past today, assume it's for tomorrow
                if (date < today) {
                    date.setDate(date.getDate() + 1);
                }
                return date;
            }
            // Fallback: return current time
            console.warn('Could not parse time string:', timeString);
            return today;
        }
        // ✅ SIMPLIFIED: Order submission using secure ID-based payload
        async function submitOrder() {
            if (!checkoutStore.canSubmit)
                return;
            loading.value = true;
            error.value = null;
            try {
                // ✅ Use RestaurantApiClient to create secure, ID-based payload
                const resolvedOnlineMethod = checkoutStore.paymentMethods?.activeOnlineProvider === 'stripe' ? 'stripe' : 'paypal';
                const selectedMethodForOrder = paymentMethod.value === 'manual'
                    ? 'cash'
                    : paymentMethod.value === 'online'
                        ? resolvedOnlineMethod
                        : paymentMethod.value;
                const orderData = apiClient.convertCartToOrderRequest(cartStore.items, customer.value, checkoutStore.orderType, {
                    method: selectedMethodForOrder,
                }, 
                // DeliveryInfo for all orders (pickup uses this for requested time too)
                {
                    // For delivery orders: include delivery address
                    ...(checkoutStore.orderType === "delivery" ? {
                        deliveryAddress: {
                            street: customer.value.address.street || "",
                            city: customer.value.address.city || "",
                            postalCode: customer.value.address.postalCode || "",
                            country: "Deutschland",
                            instructions: deliveryAdditionalInfo.value || ""
                        }
                    } : {}),
                    // For all orders: include requested time
                    ...(getSelectedTimeForOrder() ? {
                        requestedDeliveryTime: parseTimeStringToDate(getSelectedTimeForOrder())
                    } : {})
                }, undefined, // specialRequests
                undefined, // captchaToken
                checkoutStore.discountCode || undefined // Discount code
                );
                // ✅ NEW: Add discount code to order data if applied
                if (checkoutStore.discountCode && checkoutStore.discountInfo?.isValid) {
                    orderData.discountCode = checkoutStore.discountCode;
                    console.log('🎟️ Adding discount code to order:', checkoutStore.discountCode);
                }
                // ✅ DEBUG: Log the secure payload format
                console.log('🔐 Secure Order Payload (IDs only):', JSON.stringify(orderData, null, 2));
                const response = await apiClient.submitOrder(orderData);
                if (response.status === 'success' && response.data) {
                    // ✅ DEBUG: Log complete response structure
                    console.log('🔍 DEBUG: Complete API response structure:', JSON.stringify(response, null, 2));
                    console.log('🔍 DEBUG: response.data:', response.data);
                    console.log('🔍 DEBUG: response.data.data:', response.data?.data);
                    // ✅ FIX: Server returns { success, data: { orderNumber, ... } }
                    // RestaurantApiClient wraps it as { status, data: <server response> }
                    // So we need response.data.data to get the actual order data
                    const orderData = response.data.data || response.data;
                    // Map response to expected format
                    createdOrder.value = {
                        orderNumber: orderData.orderNumber,
                        orderId: orderData.orderId,
                        totalAmount: orderData.totalAmount,
                        totalAmountCent: orderData.totalAmountCent,
                        totalFormatted: orderData.totalFormatted,
                        status: orderData.status,
                        estimatedDeliveryTime: orderData.estimatedDeliveryTime,
                        orderType: orderData.orderType
                    };
                    orderCreated.value = true;
                    cartStore.clearCartAfterOrder();
                    checkoutStore.clearOrderData(); // ✅ Clear order data but keep customer info
                    // ✅ FIX: Use createdOrder.value for redirect
                    console.log('🔍 DEBUG: Created order object:', createdOrder.value);
                    console.log('🔍 DEBUG: Order number for redirect:', createdOrder.value.orderNumber);
                    if (!createdOrder.value.orderNumber) {
                        console.error('❌ ERROR: orderNumber is missing!', {
                            response,
                            createdOrder: createdOrder.value
                        });
                        throw new Error('Order number missing in response');
                    }
                    const redirectUrl = `/order-status/${createdOrder.value.orderNumber}`;
                    console.log('✅ DEBUG: Redirect URL:', redirectUrl);
                    await navigateTo(redirectUrl);
                }
                else {
                    throw new Error(response.message || "Failed to create order");
                }
            }
            catch (err) {
                console.error("Order submit error", err);
                error.value = err.message || "Fehler beim Erstellen der Bestellung. Bitte versuche es erneut.";
            }
            finally {
                loading.value = false;
            }
        }
        // Simplified PayPal handlers
        async function submitOrderWithCaptcha(captchaToken) {
            console.log("CAPTCHA token:", captchaToken);
            await submitOrder();
        }
        async function handlePayPalSuccess(paymentData) {
            const provider = paymentData?.provider === 'stripe' ? 'stripe' : 'paypal';
            paymentMethod.value = provider;
            console.log(`✅ ${provider} payment successful:`, paymentData);
            console.log("🔍 [handlePayPalSuccess] Payment data keys:", Object.keys(paymentData));
            console.log("🔍 [handlePayPalSuccess] Order number:", paymentData.orderNumber);
            console.log("🔍 [handlePayPalSuccess] Internal order number:", paymentData.internalOrderNumber);
            // ✅ FIX: Order was already created in createPayPalOrder and confirmed in capturePayPalOrder
            // ❌ DON'T call submitOrder() again - it would create a DUPLICATE order!
            // ✅ Instead: Navigate directly to order status page
            try {
                loading.value = true;
                // Get order number from PayPal capture response
                const orderNumber = paymentData.orderNumber || paymentData.internalOrderNumber;
                if (orderNumber) {
                    console.log(`✅ [handlePayPalSuccess] Redirecting to order status: ${orderNumber}`);
                    // Clear cart and checkout data
                    console.log("🧹 [handlePayPalSuccess] Clearing cart and checkout data...");
                    cartStore.clearCartAfterOrder();
                    checkoutStore.clearOrderData();
                    // Navigate to order status page
                    console.log(`🚀 [handlePayPalSuccess] Navigating to /order-status/${orderNumber}...`);
                    await navigateTo(`/order-status/${orderNumber}`);
                    console.log(`✅ [handlePayPalSuccess] Navigation completed`);
                }
                else {
                    console.error('❌ No order number in PayPal response:', paymentData);
                    console.error('❌ Full payment data:', JSON.stringify(paymentData, null, 2));
                    throw new Error('Bestellnummer fehlt in PayPal-Antwort');
                }
            }
            catch (err) {
                console.error("❌ Error handling PayPal success:", err);
                console.error("❌ Error stack:", err.stack);
                error.value = err.message || "Fehler beim Verarbeiten der PayPal-Zahlung";
            }
            finally {
                loading.value = false;
            }
        }
        function handlePayPalError(errorMessage) {
            error.value = `PayPal-Fehler: ${errorMessage}`;
        }
        function handlePaymentCancel(data) {
            error.value = null;
            console.log("🚫 Payment was cancelled", data);
            console.log("🔍 Cancel data received:", JSON.stringify(data));
            // ✅ NEW: Cancel the pre-created order on server
            if (data?.orderID) {
                console.log(`🚫 Cancelling server-side order for PayPal ID: ${data.orderID}`);
                // Call API to cancel the order using RestaurantApiClient
                apiClient.cancelPayPalOrder(data.orderID)
                    .then((result) => {
                    console.log('✅ Order cancelled on server:', result);
                    // Show user feedback
                    console.log('💬 Bestellung wurde storniert');
                })
                    .catch((err) => {
                    console.error('❌ Failed to cancel order on server:', err);
                    console.warn('⚠️ Order will be auto-cancelled by cleanup service after 15 minutes');
                    // Don't show error to user - order will be auto-cancelled by cleanup job
                });
            }
            else {
                console.warn('⚠️ PayPal cancelled without orderID - may have been cancelled before order creation');
            }
        }
        // Timeout error handling functions
        async function retryLoadingPricing() {
            console.log('🔄 Retrying pricing calculation after error...');
            loadingTimeout.value = false;
            // ✅ CRITICAL: Sync address before retry
            syncAddressToCart();
            try {
                if (cartStore.items.length > 0) {
                    await checkoutStore.calculatePricing();
                    // Check retry success via central requestStatus
                    if (checkoutStore.requestStatus === 200) {
                        initialLoading.value = false; // Only hide on success
                        console.log('✅ Retry successful - loading screen hidden', {
                            requestStatus: checkoutStore.requestStatus
                        });
                    }
                    else {
                        console.error('❌ Retry failed - request status:', checkoutStore.requestStatus);
                    }
                    updateRestaurantStatus();
                }
                else {
                    initialLoading.value = false;
                }
            }
            catch (error) {
                console.error('❌ Retry failed:', error);
                // Handle network errors specifically
                if (error.message?.includes('ERR_CONNECTION_REFUSED')) {
                    loadingTimeout.value = true; // Back to timeout screen for network errors
                }
                // Other error handling is done centrally in useOrderPricing
            }
        }
        // ✅ WATCH PRICING DATA: Ensure timeSlotConfig gets updated when API changes
        watch(() => checkoutStore.pricingData?.value, (newPricingData, oldPricingData) => {
            console.log('🔄 CheckoutPage: PricingData changed - timeSlotConfig will update:', {
                hasNewData: !!newPricingData,
                hasOldData: !!oldPricingData,
                newDeliveryData: newPricingData?.data?.delivery,
                hasEstimatedPickupTime: !!newPricingData?.data?.delivery?.estimatedPickupTime,
                hasEstimatedDeliveryTime: !!newPricingData?.data?.delivery?.estimatedDeliveryTime,
                estimatedPickupDisplay: newPricingData?.data?.delivery?.estimatedPickupTime?.display,
                estimatedDeliveryDisplay: newPricingData?.data?.delivery?.estimatedDeliveryTime?.display,
                modalIsOpen: timeModalOpen.value,
                trigger: 'CheckoutPage_pricingData_watch'
            });
            // Force timeSlotConfig reactivity by checking if modal is open
            if (timeModalOpen.value && newPricingData && newPricingData !== oldPricingData) {
                console.log('🎯 Modal is open during pricingData change - will trigger timeSlotConfig update');
            }
        }, { immediate: true, deep: true });
        // ✅ WATCH OPENING HOURS: Debug and ensure reactivity
        watch(() => checkoutStore.openingHoursInfo, (newHoursInfo, oldHoursInfo) => {
            console.log('🕐 CheckoutPage: Opening hours info changed:', {
                newValue: newHoursInfo,
                oldValue: oldHoursInfo,
                isWithinHours: newHoursInfo?.isWithinHours,
                status: newHoursInfo?.status,
                message: newHoursInfo?.message,
                hasChanged: newHoursInfo !== oldHoursInfo
            });
        }, { immediate: true, deep: true });
        // Cleanup event listener
        onUnmounted(() => {
            window.removeEventListener('orderPricingLoading', handlePricingLoadingEvent);
            window.removeEventListener('operatingHoursAvailable', handleOperatingHoursEvent);
        });
        return (_ctx, _cache) => {
            const _component_router_link = _resolveComponent("router-link");
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createElementVNode("div", _hoisted_1, [
                    _createCommentVNode(" Loading Screen for Initial Request "),
                    (initialLoading.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            _createElementVNode("div", _hoisted_3, [
                                _createCommentVNode(" Request Failed Error State "),
                                (_unref(checkoutStore).requestStatus !== null && _unref(checkoutStore).requestStatus !== 200)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                        _createElementVNode("div", {
                                            class: "w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full",
                                            style: _normalizeStyle(warningBadgeStyle.value)
                                        }, [
                                            (_openBlock(), _createElementBlock("svg", {
                                                class: "w-8 h-8",
                                                style: _normalizeStyle({ color: warningColor.value }),
                                                fill: "none",
                                                stroke: "currentColor",
                                                viewBox: "0 0 24 24"
                                            }, [...(_cache[14] || (_cache[14] = [
                                                    _createElementVNode("path", {
                                                        "stroke-linecap": "round",
                                                        "stroke-linejoin": "round",
                                                        "stroke-width": "2",
                                                        d: "M6 18L18 6M6 6l12 12"
                                                    }, null, -1 /* CACHED */)
                                                ]))], 4 /* STYLE */))
                                        ], 4 /* STYLE */),
                                        _createElementVNode("h2", {
                                            class: "text-xl font-semibold mb-2",
                                            style: _normalizeStyle({ color: warningColor.value })
                                        }, "Anfrage fehlgeschlagen", 4 /* STYLE */),
                                        _createElementVNode("p", _hoisted_5, [
                                            _createTextVNode(" Der Server hat die Anfrage abgelehnt (Status: " + _toDisplayString(_unref(checkoutStore).requestStatus) + "). ", 1 /* TEXT */),
                                            (_unref(checkoutStore).requestStatus === 404)
                                                ? (_openBlock(), _createElementBlock("span", _hoisted_6, "Die angeforderte Ressource wurde nicht gefunden."))
                                                : (_unref(checkoutStore).requestStatus === 500)
                                                    ? (_openBlock(), _createElementBlock("span", _hoisted_7, "Es ist ein Serverfehler aufgetreten."))
                                                    : (_unref(checkoutStore).requestStatus >= 400 && _unref(checkoutStore).requestStatus < 500)
                                                        ? (_openBlock(), _createElementBlock("span", _hoisted_8, "Die Anfrage war fehlerhaft."))
                                                        : (_unref(checkoutStore).requestStatus === 0)
                                                            ? (_openBlock(), _createElementBlock("span", _hoisted_9, "Keine Verbindung zum Server."))
                                                            : (_openBlock(), _createElementBlock("span", _hoisted_10, "Unbekannter Fehler aufgetreten."))
                                        ]),
                                        _createElementVNode("button", {
                                            onClick: retryLoadingPricing,
                                            class: "text-white px-6 py-2 rounded-lg transition-colors",
                                            style: _normalizeStyle(primaryButtonStyle.value)
                                        }, " Erneut versuchen ", 4 /* STYLE */)
                                    ]))
                                    : (loadingTimeout.value)
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                            _createCommentVNode(" Timeout Error State "),
                                            _createElementVNode("div", _hoisted_11, [
                                                _createElementVNode("div", {
                                                    class: "w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full",
                                                    style: _normalizeStyle(warningBadgeStyle.value)
                                                }, [
                                                    (_openBlock(), _createElementBlock("svg", {
                                                        class: "w-8 h-8",
                                                        style: _normalizeStyle({ color: warningColor.value }),
                                                        fill: "none",
                                                        stroke: "currentColor",
                                                        viewBox: "0 0 24 24"
                                                    }, [...(_cache[15] || (_cache[15] = [
                                                            _createElementVNode("path", {
                                                                "stroke-linecap": "round",
                                                                "stroke-linejoin": "round",
                                                                "stroke-width": "2",
                                                                d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                                            }, null, -1 /* CACHED */)
                                                        ]))], 4 /* STYLE */))
                                                ], 4 /* STYLE */),
                                                _createElementVNode("h2", {
                                                    class: "text-xl font-semibold mb-2",
                                                    style: _normalizeStyle({ color: warningColor.value })
                                                }, "Verbindung unterbrochen", 4 /* STYLE */),
                                                _cache[16] || (_cache[16] = _createElementVNode("p", { class: "text-gray-600 mb-4" }, "Die Verbindung zum Server ist fehlgeschlagen. Bitte überprüfe deine Internetverbindung oder versuche es später erneut.", -1 /* CACHED */)),
                                                _createElementVNode("button", {
                                                    onClick: retryLoadingPricing,
                                                    class: "text-white px-6 py-2 rounded-lg transition-colors",
                                                    style: _normalizeStyle(primaryButtonStyle.value)
                                                }, " Erneut versuchen ", 4 /* STYLE */)
                                            ])
                                        ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
                                        : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                            _createCommentVNode(" Loading State "),
                                            _createElementVNode("div", null, [
                                                _createElementVNode("div", {
                                                    class: "animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4",
                                                    style: _normalizeStyle(spinnerStyle.value)
                                                }, null, 4 /* STYLE */),
                                                _cache[17] || (_cache[17] = _createElementVNode("h2", { class: "text-xl font-semibold text-gray-700 mb-2" }, "Bestellung wird vorbereitet...", -1 /* CACHED */)),
                                                _cache[18] || (_cache[18] = _createElementVNode("p", { class: "text-gray-500 mb-4" }, "Preise und Verfügbarkeit werden überprüft", -1 /* CACHED */)),
                                                _cache[19] || (_cache[19] = _createElementVNode("div", { class: "text-xs text-gray-400 mt-4" }, " Falls das Laden zu lange dauert oder ein Fehler angezeigt wird, kontaktiere bitte das Restaurant. ", -1 /* CACHED */))
                                            ])
                                        ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
                            ])
                        ]))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _createCommentVNode(" Main Content - only show when loading is complete "),
                            _createElementVNode("div", _hoisted_12, [
                                _cache[22] || (_cache[22] = _createElementVNode("h1", { class: "hidden sm:block text-3xl font-bold mb-8 text-gray-700 text-center" }, " Bestellung abschließen ", -1 /* CACHED */)),
                                _createCommentVNode(" Empty Cart "),
                                (_unref(cartStore)?.isEmpty)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                        _cache[21] || (_cache[21] = _createTextVNode(" Dein Warenkorb ist leer. ", -1 /* CACHED */)),
                                        _createVNode(_component_router_link, {
                                            to: "/menu",
                                            class: "hover:underline",
                                            style: _normalizeStyle(menuLinkStyle.value)
                                        }, {
                                            default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                                                    _createTextVNode("Zur Speisekarte", -1 /* CACHED */)
                                                ]))]),
                                            _: 1 /* STABLE */
                                        }, 8 /* PROPS */, ["style"])
                                    ]))
                                    : (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                        _createCommentVNode(" Linke Spalte: Bestellübersicht "),
                                        _createElementVNode("div", _hoisted_15, [
                                            _createVNode(_unref(OrderOverview), {
                                                items: _unref(cartStore).items,
                                                subtotal: Number(_unref(checkoutStore).subtotal) || 0,
                                                "delivery-fee": Number(_unref(checkoutStore).deliveryFee) || 0,
                                                "service-fee": Number(_unref(checkoutStore).serviceFee) || 0,
                                                "total-amount": Number(_unref(checkoutStore).total) || 0,
                                                "order-type": _unref(checkoutStore).orderType,
                                                "format-price": formatPrice,
                                                "discount-info": _unref(checkoutStore).discountInfo ?? undefined,
                                                "tax-breakdown": _unref(checkoutStore).taxBreakdown,
                                                "tax-calculation": _unref(checkoutStore).taxCalculation,
                                                breakdown: {},
                                                onUpdateQuantity: updateItemQuantity,
                                                onRemoveItem: removeItem
                                            }, null, 8 /* PROPS */, ["items", "subtotal", "delivery-fee", "service-fee", "total-amount", "order-type", "discount-info", "tax-breakdown", "tax-calculation"])
                                        ]),
                                        _createCommentVNode(" Rechte Spalte: Checkout Details "),
                                        _createElementVNode("div", null, [
                                            _createVNode(_unref(CheckoutRightSidebar), {
                                                "order-type": localOrderType.value,
                                                "onUpdate:orderType": _cache[0] || (_cache[0] = ($event) => ((localOrderType).value = $event)),
                                                customer: customer.value,
                                                "onUpdate:customer": _cache[1] || (_cache[1] = ($event) => ((customer).value = $event)),
                                                "time-selection": timeSelection.value,
                                                "onUpdate:timeSelection": _cache[2] || (_cache[2] = ($event) => ((timeSelection).value = $event)),
                                                "payment-method": paymentMethod.value,
                                                "onUpdate:paymentMethod": _cache[3] || (_cache[3] = ($event) => ((paymentMethod).value = $event)),
                                                "delivery-additional-info": deliveryAdditionalInfo.value,
                                                "can-submit": _unref(checkoutStore).canSubmit,
                                                loading: loading.value,
                                                "total-amount": _unref(checkoutStore).total,
                                                "format-price": formatPrice,
                                                error: error.value || '',
                                                "order-created": orderCreated.value,
                                                "created-order": createdOrder.value,
                                                "opening-hours-validation": _unref(checkoutStore).openingHoursInfo,
                                                "is-blocked-by-opening-hours": isRestaurantClosed.value || (_unref(checkoutStore).openingHoursInfo && !_unref(checkoutStore).openingHoursInfo.isWithinHours),
                                                "order-data": {
                                                    items: _unref(cartStore).items,
                                                    customer: customer.value,
                                                    orderType: _unref(checkoutStore).orderType,
                                                    timeSelection: timeSelection.value,
                                                    paymentMethod: paymentMethod.value,
                                                    deliveryFee: _unref(checkoutStore).deliveryFee,
                                                    serviceFee: _unref(checkoutStore).serviceFee,
                                                    totalAmount: _unref(checkoutStore).total,
                                                    subtotal: _unref(checkoutStore).subtotal,
                                                },
                                                subtotal: _unref(checkoutStore).subtotal,
                                                "delivery-fee": _unref(checkoutStore).deliveryFee,
                                                "service-fee": _unref(checkoutStore).serviceFee,
                                                onOpenContactModal: _cache[4] || (_cache[4] = ($event) => (contactModalOpen.value = true)),
                                                onOpenAddressModal: _cache[5] || (_cache[5] = ($event) => (addressModalOpen.value = true)),
                                                onOpenTimeModal: _cache[6] || (_cache[6] = ($event) => (timeModalOpen.value = true)),
                                                onPaymentMethodSelected: handlePaymentMethodSelected,
                                                onSubmit: submitOrder,
                                                onCaptchaSubmit: submitOrderWithCaptcha,
                                                onPaymentSuccess: handlePayPalSuccess,
                                                onPaymentError: handlePayPalError,
                                                onPaymentCancel: handlePaymentCancel
                                            }, null, 8 /* PROPS */, ["order-type", "customer", "time-selection", "payment-method", "delivery-additional-info", "can-submit", "loading", "total-amount", "error", "order-created", "created-order", "opening-hours-validation", "is-blocked-by-opening-hours", "order-data", "subtotal", "delivery-fee", "service-fee"])
                                        ])
                                    ]))
                            ])
                        ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */)),
                    _createCommentVNode(" End of main content v-else ")
                ]),
                _createCommentVNode(" Modals "),
                _createVNode(_unref(ContactDataModal), {
                    "is-open": contactModalOpen.value,
                    modelValue: customer.value,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((customer).value = $event)),
                    "onUpdate:isOpen": _cache[8] || (_cache[8] = ($event) => (contactModalOpen.value = $event))
                }, null, 8 /* PROPS */, ["is-open", "modelValue"]),
                _createVNode(_unref(DeliveryAddressModal), {
                    "is-open": addressModalOpen.value,
                    modelValue: customer.value.address,
                    "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event) => ((customer.value.address) = $event)),
                    "additional-info": deliveryAdditionalInfo.value,
                    "onUpdate:additionalInfo": _cache[10] || (_cache[10] = ($event) => (deliveryAdditionalInfo.value = $event)),
                    "onUpdate:isOpen": _cache[11] || (_cache[11] = ($event) => (addressModalOpen.value = $event)),
                    onSaved: handleAddressSaved
                }, null, 8 /* PROPS */, ["is-open", "modelValue", "additional-info"]),
                _createVNode(_unref(TimeSelectionModalStore), {
                    "is-open": timeModalOpen.value,
                    modelValue: timeSelection.value,
                    "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event) => ((timeSelection).value = $event)),
                    "onUpdate:isOpen": _cache[13] || (_cache[13] = ($event) => (timeModalOpen.value = $event))
                }, null, 8 /* PROPS */, ["is-open", "modelValue"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
